import { useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import products from '../../../Data/products'
import './ProductPage.css'

const WHATSAPP_BASE = 'https://wa.me/573187260291?text='

function buildWhatsappUrl(productName) {
    const message = `¡Hola Santiago! Vi ${productName} en tu portafolio y me interesa saber más.`
    return `${WHATSAPP_BASE}${encodeURIComponent(message)}`
}

/**
 * Actualiza el <head> del documento en navegación cliente. En la carga directa
 * de la URL el servidor Express ya inyecta el meta correcto (ver server.js);
 * esto mantiene el título/description al navegar dentro de la SPA.
 */
function useProductHead(product) {
    useEffect(() => {
        if (!product) return
        const prevTitle = document.title
        const canonical = `https://www.santirev.com/${product.slug}`

        document.title = product.seo.title

        const setMeta = (selector, attr, value) => {
            let el = document.head.querySelector(selector)
            if (!el) {
                el = document.createElement('meta')
                const [key, val] = selector.replace(/meta\[|\]/g, '').split('=')
                el.setAttribute(key, val.replace(/["']/g, ''))
                document.head.appendChild(el)
            }
            el.setAttribute(attr, value)
        }

        setMeta('meta[name="description"]', 'content', product.seo.description)
        setMeta('meta[property="og:title"]', 'content', product.seo.title)
        setMeta('meta[property="og:description"]', 'content', product.seo.description)
        setMeta('meta[property="og:url"]', 'content', canonical)

        let link = document.head.querySelector('link[rel="canonical"]')
        if (link) link.setAttribute('href', canonical)

        return () => {
            document.title = prevTitle
        }
    }, [product])
}

export default function ProductPage() {
    const { slug } = useParams()
    const product = products.find((p) => p.slug === slug)

    useProductHead(product)

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [slug])

    if (!product) {
        return (
            <section className="product-page section">
                <div className="container product-page__notfound">
                    <h1 className="section__title">Producto no encontrado</h1>
                    <p>El producto que buscas no existe o cambió de dirección.</p>
                    <Link to="/#software" className="button">Ver todos los productos</Link>
                </div>
            </section>
        )
    }

    const whatsappUrl = buildWhatsappUrl(product.name)

    return (
        <article className="product-page">
            {/* HERO */}
            <header className="product-page__hero section">
                <div className="container product-page__hero-inner">
                    <div className="product-page__hero-text">
                        <Link to="/#software" className="product-page__back">
                            <i className="bx bx-arrow-back" aria-hidden="true"></i> Todos los productos
                        </Link>
                        {/*<span className="product-page__status">*/}
                        {/*    <span className="product-page__status-dot"></span> {product.status}*/}
                        {/*</span>*/}
                        <h1 className="product-page__title">{product.name}</h1>
                        <p className="product-page__tagline">{product.tagline}</p>
                        <p className="product-page__intro">{product.intro}</p>
                        <div className="product-page__cta-row">
                            <a href={whatsappUrl} target="_blank" rel="noreferrer" className="button product-page__cta">
                                <i className="bx bxl-whatsapp" aria-hidden="true"></i> Quiero esto en mi negocio
                            </a>
                            {product.liveUrl && (
                                <a href={product.liveUrl} target="_blank" rel="noreferrer" className="product-page__live">
                                    Ver en vivo <i className="bx bx-link-external" aria-hidden="true"></i>
                                </a>
                            )}
                        </div>
                    </div>
                    <div className="product-page__hero-media">
                        <img
                            src={`assets/img/${product.img}`}
                            alt={`${product.name}: ${product.tagline}`}
                            className="product-page__img"
                            width="600"
                            height="400"
                        />
                    </div>
                </div>
            </header>

            {/* PROBLEMA / SOLUCIÓN */}
            <section className="product-page__section section">
                <div className="container product-page__ps">
                    <div className="product-page__ps-card">
                        <h2 className="product-page__ps-title">
                            <i className="bx bx-error-circle" aria-hidden="true"></i> El problema
                        </h2>
                        <p>{product.problem}</p>
                    </div>
                    <div className="product-page__ps-card product-page__ps-card--solution">
                        <h2 className="product-page__ps-title">
                            <i className="bx bx-check-shield" aria-hidden="true"></i> La solución
                        </h2>
                        <p>{product.solution}</p>
                    </div>
                </div>
            </section>

            {/* FEATURES */}
            <section className="product-page__section section">
                <div className="container">
                    <h2 className="section__title product-page__h2">Qué incluye</h2>
                    <div className="product-page__features">
                        {product.features.map((feature) => (
                            <div key={feature} className="product-page__feature">
                                <i className="bx bx-check-circle" aria-hidden="true"></i>
                                <span>{feature}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CASOS DE USO */}
            <section className="product-page__section section">
                <div className="container">
                    <h2 className="section__title product-page__h2">Casos de uso</h2>
                    <ul className="product-page__usecases">
                        {product.useCases.map((useCase) => (
                            <li key={useCase} className="product-page__usecase">
                                <i className="bx bx-right-arrow-alt" aria-hidden="true"></i>
                                <span>{useCase}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* STACK */}
            <section className="product-page__section section">
                <div className="container product-page__stack-wrap">
                    <h2 className="section__title product-page__h2">Tecnología</h2>
                    <div className="product-page__stack">
                        {product.stack.map((tech) => (
                            <span key={tech} className="product-page__chip">{tech}</span>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA FINAL */}
            <section className="product-page__final section">
                <div className="container product-page__final-inner">
                    <h2 className="product-page__final-title">¿Quieres {product.name} funcionando en tu negocio?</h2>
                    <p className="product-page__final-text">
                        Cuéntame qué necesitas y te muestro cómo adaptarlo a tu caso.
                    </p>
                    <a href={whatsappUrl} target="_blank" rel="noreferrer" className="button product-page__cta">
                        <i className="bx bxl-whatsapp" aria-hidden="true"></i> Hablemos por WhatsApp
                    </a>
                    <Link to="/#software" className="product-page__back product-page__back--center">
                        <i className="bx bx-arrow-back" aria-hidden="true"></i> Ver otros productos
                    </Link>
                </div>
            </section>
        </article>
    )
}
